import { Trans, t } from '@lingui/macro';
import { useEffect, useReducer, useState } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { Trash2, Loader2 } from 'lucide-react';

import { ExternalShareLink, ExternalShareLinkDownload } from '../../models/ExternalShareLink';
import { Room } from '../../models/Room';
import { RoomItem } from '../../models/RoomItem';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { getDisplayName } from '../../services/Utils/userDisplayName';
import { ShareLinkModal } from '../components/ShareLinkModal';

import { Button } from "../../components/ui/button";
import { UserAvatar } from '../components/UserAvatar';
import { cn } from "../../lib/utils";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";


const ExternalFileSharePage: React.FC<{ room: Room, submodal: (v: boolean) => void }> = ({ room, submodal }) => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [links, setLinks] = useState<ExternalShareLink[]>([]);
  const [downloads, setDownloads] = useState<ExternalShareLinkDownload[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<ExternalShareLink>();
  const [items, setItems] = useState<RoomItem[]>([]);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  // 삭제 다이얼로그
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<ExternalShareLink | null>(null);

  useEffect(() => {
    setLinks([]);
    const unsub1 = Fire.item.subscribeToRoomItemsByType(room.id, ['file'], items => {
      setItems(items);
    });

    const unsub2 = Fire.item.subscribeToExternalShareLinks(room.id, (links) => {
      setLinks(links);
    });

    return () => {
      unsub1();
      unsub2();
    }
  }, []);

  useEffect(() => {
    if (selectedDraft) {
      submodal(true);
      return Fire.item.subscribeToExternalShareDownloads(selectedDraft, downs => {
        setDownloads(downs);
      })
    }
    else {
      submodal(false);
    }
  }, [selectedDraft])

  function changeSelectedLink(link: ExternalShareLink | undefined) {
    setSelectedDraft(link ? Util.pojo(link) : undefined);
  }

  function handleDeleteClick(link: ExternalShareLink) {
    setLinkToDelete(link);
    setShowDeleteDialog(true);
  }

  async function confirmDelete() {
    if (linkToDelete) {
      await Fire.item.deleteExternalShareLink(linkToDelete);
      changeSelectedLink(undefined);
    }
    setShowDeleteDialog(false);
    setLinkToDelete(null);
  }

  function getLinkTitle(link: ExternalShareLink): string {
    if (link.title) return link.title;
    if (link.roomItems.length > 0) {
      return Util.getTruncatedFileName(link.roomItems[0].title, 100) +
        (link.roomItems.length > 1 ? t` and ${link.roomItems.length - 1} other files` : '');
    }
    return t`No files added`;
  }

  return (
    <div className="flex flex-col flex-1 h-full min-h-0 relative">
      {/* 상단 여백 */}
      <div className="h-4 shrink-0" />

      <div className="flex flex-col flex-1 min-h-0 bg-background overflow-hidden relative">
        {/* 로딩 인디케이터 */}
        {inProgress && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* 테이블 헤더 - RoomFilesModal의 text-meta-sm 헤더 양식 준수 */}
        <div className="flex items-center px-4 h-8 bg-[rgb(var(--tbl-bg-header))] border-b border-[rgb(var(--tbl-border))] text-meta-sm font-semibold text-[rgb(var(--tbl-text-secondary))] select-none shrink-0" data-node-id="share-link-header">

          <div className="flex-[4] min-w-0 pr-4 pl-1"><Trans>Title</Trans></div>
          <div className="hidden sm:flex shrink-0 items-center justify-end">
            <div className="w-[144px] shrink-0 px-4"><Trans>Creator</Trans></div>
            <div className="w-[160px] shrink-0 px-4 text-left"><Trans>Created Date</Trans></div>
            <div className="w-[160px] shrink-0 px-4 text-left"><Trans>Expire Date</Trans></div>
            <div className="w-8 shrink-0"></div> {/* Action Placeholder */}
          </div>
        </div>

        {/* 링크 목록 - FileListItem 디자인 규격 (px-3 py-2) 준수 */}
        <div className="flex-1 overflow-y-auto bg-background">
          {links.length === 0 && !inProgress && (
            <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
              <Trans>No links in this channel.</Trans>
            </div>
          )}

          {links.map(link => (
            <div
              key={link.id}
              className={cn(
                "flex items-center px-4 py-2 hover:bg-[rgb(var(--tbl-bg-hover))] border-b border-[rgb(var(--tbl-divider))] transition-all cursor-pointer group",
                selectedDraft?.id === link.id ? "bg-primary/5" : "bg-background"
              )}
              onClick={() => changeSelectedLink(link)}
            >


              {/* 제목 영역 (FileListItem text-sm, pl-1) */}
              <div className="flex-[4] min-w-0 pl-1 pr-4">
                <p className="text-sm font-normal text-[rgb(var(--tbl-text-primary))] truncate group-hover:text-primary transition-colors">
                  {getLinkTitle(link)}
                </p>
                {/* 모바일만 보이는 정보 */}
                <div className="sm:hidden flex items-center gap-2 mt-1 text-meta-sm text-[rgb(var(--tbl-text-secondary))]">
                  <span className="text-label">{getDisplayName(link.user)}</span>
                  <span className="opacity-50">•</span>
                  <span>{UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}</span>
                </div>
              </div>

              {/* 고정 너비 정보 영역 (sm 이상) - FileListItem과 열 간격 일치 */}
              <div className="hidden sm:flex shrink-0 items-center justify-end">
                {/* 작성자 */}
                <div className="w-[144px] shrink-0 flex items-center gap-1.5 px-4">
                  <UserAvatar src={link.user.photoUrl} name={getDisplayName(link.user)} className="w-5 h-5" />
                  <div className="text-sm text-label truncate">{getDisplayName(link.user)}</div>
                </div>

                {/* 생성일 */}
                <div className="w-[160px] shrink-0 text-sm text-[rgb(var(--tbl-text-secondary))] whitespace-nowrap overflow-hidden text-ellipsis px-4">
                  {UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}
                </div>

                {/* 만료일 */}
                <div className="w-[160px] shrink-0 text-sm text-[rgb(var(--tbl-text-secondary))] truncate px-4">
                  {link.expireDate ? UtilApp.dateStrToTimeAndDay(link.expireDate, My.user.lang) : '-'}
                </div>
              </div>

              {/* 삭제 버튼 — 모바일/PC 공통 표시 */}
              <div className="w-8 flex items-center justify-center shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(link);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete link`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Are you sure you want to delete this link?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
              {t`Delete`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ShareLinkModal */}
      {selectedDraft && room && (
        <ShareLinkModal
          show={true}
          isNew={false}
          setShow={() => setSelectedDraft(undefined)}
          link={selectedDraft}
          room={room}
          downloads={downloads}
          items={items}
          onDone={() => { }}
        />
      )}
    </div>
  );
}

export default ExternalFileSharePage;
